import { Caption, VTooltip, VTooltipContent, VTooltipTrigger } from '@vrzl/ui';
import { AlertTriangle, Info } from 'lucide-react';

import { pagesLabel } from '@/domains/overview';
import type { ValidationAnswer, ValidationReportItem } from '@/domains/report/api/types';

import { evidenceQuotes } from '../utils/evidenceQuotes';

type EvidenceInfoProps = {
  answer: ValidationAnswer;
  documents: ValidationReportItem['documents'];
};

/**
 * Icoon vóór een waarde, met de bewijslast in de tooltip: elk citaat met document en pagina's.
 * Zonder citaat een waarschuwingsicoon, zodat ontbrekend bewijs opvalt.
 */
export function EvidenceInfo({ answer, documents }: EvidenceInfoProps) {
  const quotes = evidenceQuotes(answer, documents);
  const hasQuotes = quotes.length > 0;
  const Icon = hasQuotes ? Info : AlertTriangle;

  return (
    <VTooltip>
      <VTooltipTrigger asChild>
        <button
          type="button"
          aria-label={hasQuotes ? 'Bewijslast voor deze waarde' : 'Geen bewijslast voor deze waarde'}
          className={`mt-0.5 inline-flex size-5 shrink-0 cursor-help items-center justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            hasQuotes ? 'text-muted-foreground' : 'text-warning-600'
          }`}
        >
          <Icon size={16} aria-hidden="true" />
        </button>
      </VTooltipTrigger>
      <VTooltipContent className="max-w-sm wrap-break-word">
        {hasQuotes ? (
          <>
            <Caption className="font-medium text-foreground">Brontekst</Caption>
            <ul className="mt-1 space-y-2">
              {quotes.map((quote, i) => {
                const meta = [quote.docName, pagesLabel(quote.pages)].filter(Boolean).join(' · ');
                return (
                  <li key={i}>
                    <Caption className="line-clamp-4 italic text-foreground">&ldquo;{quote.text}&rdquo;</Caption>
                    {meta && <Caption>{meta}</Caption>}
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <>
            <Caption className="font-medium text-foreground">Geen bewijslast</Caption>
            <Caption>Er is geen citaat uit de documenten voor deze waarde.</Caption>
          </>
        )}
      </VTooltipContent>
    </VTooltip>
  );
}
